import {
	Backdrop,
	createStyles,
	Fade,
	makeStyles,
	Modal,
} from "@material-ui/core";
import moment from "moment";
import React, { memo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import buttonClose from "../../../assets/kinologyIcons/svg/close.svg";
import { ReactComponent as CheckIcon } from "../../../assets/kinologyIcons/svg/check.svg";
import { TextsProps } from "../../../database/txt.interface";
import { UserProps } from "../../../interfaces/pacientes.interface";
import { AppActions } from "../../../store/ducks/app";
import { Colors } from "../../../styles/colors";
import { sendErrorSentryOrLocalHost } from "../../../utils/funcs";
import { TypesErrorRequest } from "../../../utils/types";
import Contract from "../../../assets/kinologyIcons/svg/Contract.svg";
import {
	ButtonConfirmar,
	Container,
	ContainerButtons,
	TextConfirmar,
} from "./styles";
import { AvaliationActions } from "../../../store/ducks/avaliation";

const useStyles = makeStyles(() =>
	createStyles({
		modal: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "1rem",
		},
	})
);

interface ModalProps {
	visible?: boolean;
	setVisibleModal?: (visible: boolean) => void;
}

const ModalAcceptTerms: React.FC<ModalProps> = ({
	visible: visibleProp,
	setVisibleModal,
}) => {
	const dispatch = useDispatch();
	const classes = useStyles();
	const isSubmittingRef = useRef(false);

	const current_language: TextsProps = useSelector(
		(state: any) => state.configs.current_language
	);
	const user_logado: UserProps = useSelector((state: any) => state.auth.user);
	const isOpenModal = useSelector(
		(state: any) => state.app.is_open_modal_accept_terms
	);

	const visible = visibleProp ?? isOpenModal;
	const canCloseModal = useSelector(
		(state: any) => state.app.can_close_modal_accept_terms
	);

	const [checkedValue, setCheckedValue] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleUseTerms = () => {
		window.open(
			"https://base.materials2.kinology.com.br/termos-de-uso-kinology",
			"_blank",
			"noopener,noreferrer"
		);
	};

	const handlePrivacyPolicy = () => {
		window.open(
			"https://kinology.com.br/termos-de-uso-e-politicas-de-privacidade/",
			"_blank",
			"noopener,noreferrer"
		);
	};

	const closeModal = () => {
		setVisibleModal?.(false);
		dispatch(AppActions.set_open_modal_accept_terms(false));
		setCheckedValue(false);
	};

	const showToast = (
		type_toast: string,
		message_toast: string,
		title?: string
	) => {
		dispatch(
			AppActions.set_toast_visible({
				status_toast: true,
				type_toast,
				message_toast: title ? `${title}: ${message_toast}` : message_toast,
			})
		);
	};

	const AcceptTerms = async (checkboxValue: boolean) => {
		if (!checkboxValue || isSubmittingRef.current) {
			return;
		}

		const data_request = {
			txDataHora: moment(new Date()).format("YYYY-MM-DD HH:mm:ss").toString(),
			TERMO_VERSAO: "1",
			TERMO_RESPOSTA: checkboxValue ? "S" : "N",
		};

		isSubmittingRef.current = true;
		setIsSubmitting(true);

		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL_V7}termos/plano_assinar`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${user_logado?.token}`,
					},
					body: JSON.stringify(data_request),
				}
			);

			const data = await response.json();

			if (response.ok && data.status === TypesErrorRequest.SUCCESS) {
				showToast(
					TypesErrorRequest.SUCCESS,
					current_language?.termo_aceito_sucesso,
					current_language?.success
				);
				dispatch(AvaliationActions.set_show_contract_plans_modal(false));
				closeModal();
				return;
			}

			showToast(
				TypesErrorRequest.FAIL,
				data?.message || current_language?.termo_respondido_erro,
				current_language?.failure
			);
		} catch (e) {
			if (canCloseModal) {
				closeModal();
			}
			showToast(
				TypesErrorRequest.FAIL,
				current_language?.termo_respondido_erro,
				current_language?.failure
			);
			sendErrorSentryOrLocalHost(e, {
				message: "Erro ao aceitar termos do plano - AcceptTerms",
				tags: {
					endpoint: "termos/plano_assinar",
					file: "src/components/Modais/ModalAcceptTerms/index.tsx",
				},
			});
		} finally {
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	const handleModalClose = () => {
		if (canCloseModal) {
			closeModal();
		}
	};

	const handleTermsLinkClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		handleUseTerms();
	};

	const handlePrivacyLinkClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		event.stopPropagation();
		handlePrivacyPolicy();
	};

	return (
		<Modal
			aria-labelledby="modal-accept-terms-title"
			aria-describedby="modal-accept-terms-description"
			className={classes.modal}
			open={visible}
			onClose={handleModalClose}
			closeAfterTransition
			disableBackdropClick={!canCloseModal}
			disableEscapeKeyDown={!canCloseModal}
			BackdropComponent={Backdrop}
			BackdropProps={{
				timeout: 500,
			}}
		>
			<Fade in={visible}>
				<Container>
					{canCloseModal && (
						<img
							className="close"
							src={buttonClose}
							onClick={handleModalClose}
							alt="Fechar"
						/>
					)}

					<img src={Contract} alt="Contrato" />

					<h2 id="modal-accept-terms-title" className="title">
						<span className="titleDesktop">
							{current_language?.update_use_terms}
						</span>
						<span className="titleMobile">
							{current_language?.update_use_terms_break}
						</span>
					</h2>

					<p id="modal-accept-terms-description" className="subtitle">
						{current_language?.politic_updated}
					</p>

					<div
						className="checkboxRow"
						role="button"
						tabIndex={0}
						onClick={() => setCheckedValue(!checkedValue)}
						onKeyDown={(event) => {
							if (event.key === "Enter" || event.key === " ") {
								event.preventDefault();
								setCheckedValue(!checkedValue);
							}
						}}
					>
						<span
							className={`checkbox ${checkedValue ? "checked" : ""}`}
							aria-hidden="true"
						>
							{checkedValue && (
								<CheckIcon
									width={14}
									height={11}
									style={{ color: Colors.white }}
								/>
							)}
						</span>

						<p className="checkboxLabel">
							{current_language?.read_and_agree}{" "}
							<button
								type="button"
								className="termsLink"
								onClick={handleTermsLinkClick}
							>
								{current_language?.use_terms}
							</button>
							{current_language?.and}
							<button
								type="button"
								className="termsLink"
								onClick={handlePrivacyLinkClick}
							>
								{current_language?.privacy_policy}
							</button>
						</p>
					</div>

					<ContainerButtons>
						<ButtonConfirmar
							$isEnabled={!!checkedValue}
							disabled={!checkedValue || isSubmitting}
							onClick={() => AcceptTerms(checkedValue)}
						>
							<TextConfirmar>{current_language?.modal_confirmar}</TextConfirmar>
						</ButtonConfirmar>
					</ContainerButtons>
				</Container>
			</Fade>
		</Modal>
	);
};

export default memo(ModalAcceptTerms);
